import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  FileBarChart,
  Save,
  ArrowLeft,
  Play,
  GripVertical,
  Plus,
  X,
  ChevronRight,
  ChevronLeft,
  Settings,
  Filter,
  Columns,
  LayoutTemplate,
  Mail,
  Calendar,
  Clock,
  Trash2
} from 'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Mock available fields for demonstration
const mockFieldsByType = {
  vendas: [
    { field_key: 'id', field_label: 'ID', field_type: 'number' },
    { field_key: 'data', field_label: 'Data', field_type: 'date' },
    { field_key: 'cliente', field_label: 'Cliente', field_type: 'text' },
    { field_key: 'produto', field_label: 'Produto', field_type: 'text' },
    { field_key: 'quantidade', field_label: 'Quantidade', field_type: 'number', is_summable: true },
    { field_key: 'valor_unitario', field_label: 'Valor Unitário', field_type: 'currency', is_summable: true },
    { field_key: 'valor_total', field_label: 'Valor Total', field_type: 'currency', is_summable: true },
    { field_key: 'status', field_label: 'Status', field_type: 'text' },
    { field_key: 'vendedor', field_label: 'Vendedor', field_type: 'text' },
    { field_key: 'forma_pagamento', field_label: 'Forma de Pagamento', field_type: 'text' },
  ],
  estoque: [
    { field_key: 'id', field_label: 'ID', field_type: 'number' },
    { field_key: 'codigo', field_label: 'Código', field_type: 'text' },
    { field_key: 'produto', field_label: 'Produto', field_type: 'text' },
    { field_key: 'categoria', field_label: 'Categoria', field_type: 'text' },
    { field_key: 'quantidade', field_label: 'Quantidade', field_type: 'number', is_summable: true },
    { field_key: 'custo_medio', field_label: 'Custo Médio', field_type: 'currency' },
    { field_key: 'valor_estoque', field_label: 'Valor em Estoque', field_type: 'currency', is_summable: true },
    { field_key: 'estoque_minimo', field_label: 'Estoque Mínimo', field_type: 'number' },
    { field_key: 'ultima_movimentacao', field_label: 'Última Movimentação', field_type: 'date' },
  ],
  shipments: [
    { field_key: 'id', field_label: 'ID', field_type: 'number' },
    { field_key: 'tracking', field_label: 'Tracking', field_type: 'text' },
    { field_key: 'data_envio', field_label: 'Data Envio', field_type: 'date' },
    { field_key: 'origem', field_label: 'Origem', field_type: 'text' },
    { field_key: 'destino', field_label: 'Destino', field_type: 'text' },
    { field_key: 'peso_kg', field_label: 'Peso (kg)', field_type: 'number', is_summable: true },
    { field_key: 'valor_frete', field_label: 'Valor Frete', field_type: 'currency', is_summable: true },
    { field_key: 'status', field_label: 'Status', field_type: 'text' },
    { field_key: 'transportadora', field_label: 'Transportadora', field_type: 'text' },
  ]
};

// Default fields for any type
const defaultFields = [
  { field_key: 'id', field_label: 'ID', field_type: 'number' },
  { field_key: 'descricao', field_label: 'Descrição', field_type: 'text' },
  { field_key: 'valor', field_label: 'Valor', field_type: 'currency', is_summable: true },
  { field_key: 'data', field_label: 'Data', field_type: 'date' },
  { field_key: 'status', field_label: 'Status', field_type: 'text' },
];

export const ReportEditor = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reportTypes, setReportTypes] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);

  // Form state
  const [reportTypeId, setReportTypeId] = useState(searchParams.get('type') || '');
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [showTotals, setShowTotals] = useState(false);
  const [showSubtotals, setShowSubtotals] = useState(false);
  const [subtotalFields, setSubtotalFields] = useState([]);
  const [orientation, setOrientation] = useState('portrait');
  
  // Schedule state
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState('daily');
  const [scheduleTime, setScheduleTime] = useState('08:00');
  const [scheduleFormat, setScheduleFormat] = useState('pdf');
  const [emailRecipients, setEmailRecipients] = useState('');

  useEffect(() => {
    fetchReportTypes();
    if (isEdit) {
      fetchReport();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (reportTypeId) {
      const selectedType = reportTypes.find(t => t.id === parseInt(reportTypeId));
      if (selectedType) {
        // Get fields for this type
        const fields = mockFieldsByType[selectedType.slug] || defaultFields;
        setAvailableFields(fields);
      }
    }
  }, [reportTypeId, reportTypes]);

  const fetchReportTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/types`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setReportTypes(data.report_types || []);
      }
    } catch (err) {
      console.error('Failed to fetch report types');
    }
  };

  const fetchReport = async () => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/reports/${id}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const report = await res.json();
        setReportTypeId(report.report_type_id?.toString() || '');
        setReportName(report.name || '');
        setReportDescription(report.description || '');
        setSelectedColumns(report.selected_columns || []);
        setFilters(report.filters || {});
        setShowTotals(report.show_totals || false);
        setShowSubtotals(report.show_subtotals || false);
        setSubtotalFields(report.subtotal_fields || []);
        setOrientation(report.orientation || 'portrait');
        
        // Load schedules if any
        if (report.schedules?.length > 0) {
          const schedule = report.schedules[0];
          setEnableSchedule(true);
          setScheduleFrequency(schedule.frequency);
          setScheduleTime(schedule.run_time?.slice(0, 5) || '08:00');
          setScheduleFormat(schedule.output_format);
          setEmailRecipients(schedule.email_recipients?.join(', ') || '');
        }
      }
    } catch (err) {
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!reportTypeId || !reportName) {
      toast.error('Preencha o tipo e nome do relatório');
      return;
    }

    if (selectedColumns.length === 0) {
      toast.error('Selecione pelo menos uma coluna');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        report_type_id: parseInt(reportTypeId),
        name: reportName,
        description: reportDescription,
        selected_columns: selectedColumns,
        filters,
        show_totals: showTotals,
        show_subtotals: showSubtotals,
        subtotal_fields: subtotalFields,
        orientation
      };

      const url = isEdit 
        ? `${API_URL}/api/report-builder/reports/${id}`
        : `${API_URL}/api/report-builder/reports`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const savedReport = await res.json();
        
        // Create/update schedule if enabled
        if (enableSchedule && emailRecipients) {
          await fetch(`${API_URL}/api/report-builder/reports/${savedReport.id}/schedules`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              report_id: savedReport.id,
              frequency: scheduleFrequency,
              run_time: scheduleTime,
              output_format: scheduleFormat,
              email_recipients: emailRecipients.split(',').map(e => e.trim()).filter(e => e)
            })
          });
        }

        toast.success(isEdit ? 'Relatório atualizado!' : 'Relatório criado!');
        navigate('/reports');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar relatório');
      }
    } catch (err) {
      toast.error('Erro ao salvar relatório');
    } finally {
      setSaving(false);
    }
  };

  const handleAddColumn = (field) => {
    if (!selectedColumns.find(c => c.field_key === field.field_key)) {
      setSelectedColumns([...selectedColumns, { ...field }]);
    }
  };

  const handleRemoveColumn = (fieldKey) => {
    setSelectedColumns(selectedColumns.filter(c => c.field_key !== fieldKey));
  };

  const handleMoveColumn = (index, direction) => {
    const newColumns = [...selectedColumns];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newColumns.length) {
      [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
      setSelectedColumns(newColumns);
    }
  };

  const handlePreview = () => {
    if (isEdit) {
      navigate(`/reports/preview/${id}`);
    } else {
      toast.info('Salve o relatório primeiro para visualizar');
    }
  };

  const unselectedFields = availableFields.filter(
    f => !selectedColumns.find(c => c.field_key === f.field_key)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-editor">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-['Space_Grotesk']">
              {isEdit ? 'Editar Relatório' : 'Criar Novo Relatório'}
            </h1>
            <p className="text-muted-foreground text-sm">
              Configure as colunas, filtros e agendamento do relatório
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePreview} disabled={!isEdit}>
            <Play className="h-4 w-4 mr-2" />
            Visualizar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-blue-500 to-indigo-600"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Basic Info & Columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileBarChart className="h-5 w-5 text-blue-600" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tipo de Relatório *</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={reportTypeId}
                    onChange={(e) => {
                      setReportTypeId(e.target.value);
                      setSelectedColumns([]);
                    }}
                  >
                    <option value="">Selecione...</option>
                    {reportTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Nome do Relatório *</Label>
                  <Input
                    placeholder="Ex: Vendas por Cliente"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrição (opcional)</Label>
                <Textarea
                  placeholder="Descreva o propósito deste relatório..."
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Column Selection */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Columns className="h-5 w-5 text-blue-600" />
                Cabeçalho (Colunas)
              </CardTitle>
              <CardDescription>
                Arraste os campos da esquerda para adicionar ao relatório
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {/* Available Fields */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Campos Disponíveis</Label>
                  <div className="border rounded-lg p-2 min-h-[300px] max-h-[400px] overflow-y-auto bg-muted/30">
                    {!reportTypeId ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        Selecione um tipo de relatório
                      </p>
                    ) : unselectedFields.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        Todos os campos foram selecionados
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {unselectedFields.map((field) => (
                          <div
                            key={field.field_key}
                            className="flex items-center justify-between p-2 rounded bg-background border cursor-pointer hover:border-blue-500 transition-colors"
                            onClick={() => handleAddColumn(field)}
                          >
                            <div className="flex items-center gap-2">
                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{field.field_label}</span>
                            </div>
                            <Button size="icon" variant="ghost" className="h-6 w-6">
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Fields */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Campos Selecionados ({selectedColumns.length})
                  </Label>
                  <div className="border rounded-lg p-2 min-h-[300px] max-h-[400px] overflow-y-auto bg-blue-50/50">
                    {selectedColumns.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        Clique nos campos à esquerda para adicionar
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {selectedColumns.map((field, index) => (
                          <div
                            key={field.field_key}
                            className="flex items-center justify-between p-2 rounded bg-background border border-blue-200"
                          >
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-4 w-4"
                                  onClick={() => handleMoveColumn(index, -1)}
                                  disabled={index === 0}
                                >
                                  <ChevronLeft className="h-3 w-3 rotate-90" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-4 w-4"
                                  onClick={() => handleMoveColumn(index, 1)}
                                  disabled={index === selectedColumns.length - 1}
                                >
                                  <ChevronRight className="h-3 w-3 rotate-90" />
                                </Button>
                              </div>
                              <span className="text-sm font-medium">{field.field_label}</span>
                              <Badge variant="outline" className="text-xs">
                                {field.field_type}
                              </Badge>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-red-500 hover:text-red-700"
                              onClick={() => handleRemoveColumn(field.field_key)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Options & Schedule */}
        <div className="space-y-6">
          {/* Display Options */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutTemplate className="h-5 w-5 text-blue-600" />
                Opções de Exibição
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Mostrar Totais</Label>
                <Switch
                  checked={showTotals}
                  onCheckedChange={setShowTotals}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Mostrar Subtotais</Label>
                <Switch
                  checked={showSubtotals}
                  onCheckedChange={setShowSubtotals}
                />
              </div>
              <div className="space-y-2">
                <Label>Orientação</Label>
                <div className="flex gap-2">
                  <Button
                    variant={orientation === 'portrait' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOrientation('portrait')}
                  >
                    Retrato
                  </Button>
                  <Button
                    variant={orientation === 'landscape' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOrientation('landscape')}
                  >
                    Paisagem
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-blue-600" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Nome do Cliente</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={filters.cliente || 'all'}
                    onChange={(e) => setFilters({ ...filters, cliente: e.target.value })}
                  >
                    <option value="all">Todos</option>
                    <option value="specific">Específico</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Período</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={filters.periodo || 'all'}
                    onChange={(e) => setFilters({ ...filters, periodo: e.target.value })}
                  >
                    <option value="all">Todos</option>
                    <option value="today">Hoje</option>
                    <option value="week">Esta Semana</option>
                    <option value="month">Este Mês</option>
                    <option value="custom">Personalizado</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Agendamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Ativar Agendamento</Label>
                <Switch
                  checked={enableSchedule}
                  onCheckedChange={setEnableSchedule}
                />
              </div>
              
              {enableSchedule && (
                <>
                  <div className="space-y-2">
                    <Label>Frequência</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={scheduleFrequency}
                      onChange={(e) => setScheduleFrequency(e.target.value)}
                    >
                      <option value="daily">Diário</option>
                      <option value="weekly">Semanal</option>
                      <option value="monthly">Mensal</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Horário</Label>
                    <Input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Formato de Envio</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={scheduleFormat}
                      onChange={(e) => setScheduleFormat(e.target.value)}
                    >
                      <option value="link">Link</option>
                      <option value="pdf">PDF Anexado</option>
                      <option value="excel">Excel Anexado</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Destinatários (Email)</Label>
                    <Textarea
                      placeholder="email1@exemplo.com, email2@exemplo.com"
                      value={emailRecipients}
                      onChange={(e) => setEmailRecipients(e.target.value)}
                      rows={2}
                    />
                    <p className="text-xs text-muted-foreground">
                      Separe múltiplos emails com vírgula
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReportEditor;
